import { Component, Prop, h, Host } from '@stencil/core';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

const COMPONENT_NAME = 'sn-cta-type-11';

interface HiddenField {
  name?: string;
  type?: string;
  value?: string;
}

interface FieldMessage {
  field?: string;
  requiredMessage?: string;
  invalidMessage?: string;
}

/**
 * Call-to-action section with a two-column layout inside a dark navy card.
 * Left column: an uppercase label, heading, supporting description, and one or two action buttons.
 * Right column (desktop only): a list of feature highlights, each with an icon, bold title, and description.
 * Differs from `sn-call-to-action-type-1` by adding a description slot beneath the heading and rendering a
 * per-item icon for each feature (instead of a fixed icon set).
 *
 * @product signnow
 * @version 1.0.0
 * @category SignNow
 * @contentConfig Call-to-action component for encouraging user engagement and conversions
 * @slot label - <me-text tag="span"> - Small uppercase label shown above the heading
 * @slot heading - {required}, <me-heading variant="2" tag="h2"> - Main CTA heading
 * @slot description - <me-text> - Supporting paragraph shown beneath the heading
 * @slot action-primary - {required}, <me-button variant="primary"> - Primary action button; opens the embedded `sn-form-type-1` "Request a demo" modal on click
 * @slot action-secondary - <me-button variant="bordered"> - Secondary action button
 * @example
 *   <sn-cta-type-11
 *     items='[{"icon":"https://marketing-static.signnow.com/347/images/me/cta-type-2/icon-1.svg","title":"Award-winning eSignature.","description":"Approve, deliver, and eSign documents to conduct business anywhere and anytime."},{"icon":"https://marketing-static.signnow.com/347/images/me/cta-type-2/icon-2.svg","title":"End-to-end online PDF editor.","description":"Create, edit, and manage PDF documents and forms in the cloud."},{"icon":"https://marketing-static.signnow.com/347/images/me/cta-type-2/icon-3.svg","title":"Online library of 85K+ state-specific legal forms.","description":"Find up-to-date legal forms and form packages for any use case in one place."}]'
 *     form-redirect-url="https://www.signnow.com/thankyou"
 *   >
 *     <me-text slot="label" tag="span" class="me-text--label">go beyond eSignatures</me-text>
 *     <me-heading slot="heading" variant="2" tag="h2">Business Cloud</me-heading>
 *     <me-text slot="description">Automate business processes with the ultimate suite of tools that are customizable for any use case.</me-text>
 *     <me-button slot="action-primary" variant="primary" tag="button">Request a demo</me-button>
 *   </sn-cta-type-11>
 */
@Component({
  tag: 'sn-cta-type-11',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SnCtaType11 {
  /**
   * Color mode for theming.
   */
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * Feature highlights shown in the right column (desktop only).
   * @type {array}
   * @item.type {object}
   * @item.property {string} icon - Icon URL (rendered via `<me-icon>`)
   * @item.property {string} title - Bold feature title
   * @item.property {string} description - Feature description text
   * @item.required icon
   * @item.required title
   * @item.content title description
   * @minItems 1
   * @maxItems 4
   */
  @Prop({ mutable: true }) items?: Array<{ icon: string; title: string; description?: string }> | string;

  /**
   * Render each feature logo on a white rounded card (matches the "Business Cloud" block).
   * When false, logos sit directly on the card background.
   * @default false
   */
  @Prop({ attribute: 'logo-background' }) logoBackground: boolean = false;

  /**
   * Show the optional "Questions or comments" field inside the embedded "Request a demo" form.
   * @default true
   */
  @Prop({ attribute: 'form-show-message' }) formShowMessage: boolean = true;

  /**
   * Form `action` URL the embedded "Request a demo" form submits to.
   * @default '#'
   */
  @Prop({ attribute: 'form-action' }) formAction: string = '#';

  /**
   * URL the user is redirected to after the embedded "Request a demo" form submits successfully.
   * When omitted, no redirect occurs.
   */
  @Prop({ attribute: 'form-redirect-url' }) formRedirectUrl?: string;

  /**
   * Extra hidden form fields posted with the embedded "Request a demo" form
   * (e.g. `industry_name`, `_token`, `gclid`). Passed straight through to the
   * embedded `sn-form-type-1` as its `hiddenFields`. Accepts a real array or a
   * JSON string of `{ name, type?, value? }` objects.
   * @type {array}
   * @formHiddenFields.type {object}
   * @formHiddenFields.property {string} name - Field name posted with the form (e.g. "gclid", "industry_name")
   * @formHiddenFields.property {string} type - Native input type for the rendered field; defaults to "hidden"
   * @formHiddenFields.property {string} value - Value posted for the field; may be empty (backend/runtime fills dynamic ones)
   * @formHiddenFields.required name
   */
  @Prop({ attribute: 'form-hidden-fields' }) formHiddenFields?: HiddenField[] | string;

  /**
   * Per-field validation error text for the embedded "Request a demo" form. Passed straight
   * through to `sn-form-type-1` as its `fieldMessages`. Each entry overrides a field's default
   * `required` / `invalid` message; unset fields keep their defaults.
   * Valid field names: `first_name`, `last_name`, `email`, `phone`, `company_name`, `job_title`, `users`.
   * @type {array}
   * @formFieldMessages.type {object}
   * @formFieldMessages.property {string} field - Target field name (e.g. "email", "first_name")
   * @formFieldMessages.property {string} requiredMessage - Error text shown when the field is left empty
   * @formFieldMessages.property {string} invalidMessage - Error text shown when the value is invalid (email field)
   * @formFieldMessages.required field
   */
  @Prop({ attribute: 'form-field-messages' }) formFieldMessages?: FieldMessage[] | string;

  private parsedItems: Array<{ icon: string; title: string; description?: string }> = [];

  componentWillLoad() {
    const parsed = parseJsonProp(this.items, []);
    this.parsedItems = Array.isArray(parsed) ? parsed : [];
  }

  render() {
    const items = this.parsedItems;
    const sectionClass = this.logoBackground ? `${COMPONENT_NAME} ${COMPONENT_NAME}--logo-background` : COMPONENT_NAME;

    return (
      <Host data-color-mode={this.colorMode}>
        <me-section class={sectionClass} yGap="2xl" yGapLg="5xl" role="region" aria-label="Call to Action">
          <div class={`${COMPONENT_NAME}__content`}>
            <me-row yGap="3xl" yGapLg="none" xGap="none" alignLg="center">
              <me-column span="12" spanLg="6" class={`${COMPONENT_NAME}__intro`}>
                <div class={`${COMPONENT_NAME}__intro-inner`}>
                  <slot name="label"></slot>
                  <slot name="heading"></slot>
                  <slot name="description"></slot>
                  <me-row
                    xGap="md"
                    xGapLg="md"
                    yGap="md"
                    yGapLg="md"
                    justify="center"
                    justifyLg="start"
                    class={`${COMPONENT_NAME}__actions`}
                  >
                    <me-column span="auto">
                      <sn-form-type-1
                        class={`${COMPONENT_NAME}__demo-form`}
                        showMessage={this.formShowMessage}
                        action={this.formAction}
                        redirectUrl={this.formRedirectUrl}
                        hiddenFields={this.formHiddenFields}
                        fieldMessages={this.formFieldMessages}
                      >
                        <slot name="action-primary" slot="trigger"></slot>
                      </sn-form-type-1>
                    </me-column>
                    <me-column span="auto">
                      <slot name="action-secondary"></slot>
                    </me-column>
                  </me-row>
                </div>
              </me-column>
              <me-column span="12" spanLg="6" class={`${COMPONENT_NAME}__media`}>
                <div class={`${COMPONENT_NAME}__media-inner`}>
                  {items.length > 0 && (
                    <ul class="cta-list">
                      {items.slice(0, 4).map(item => (
                        <li class="cta-list__item">
                          <div class="cta-list__logo">
                            <me-icon name={item.icon} />
                          </div>
                          <div class="cta-list__description">
                            <strong>{item.title}</strong>
                            {item.description ? ` ${item.description}` : ''}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </me-column>
            </me-row>
          </div>
        </me-section>
      </Host>
    );
  }
}
